import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { useRecoilState } from "recoil";
import { reportIDSelector } from "../../store/selectors";
import { makePayment } from "../../data/request";
import "./Stripe.css";

const CardField = ({ onChange }) => (
  <div className="FormRow">
    <CardElement
      options={{
        iconStyle: "solid",
        style: {
          base: {
            iconColor: "rgba(0,0,0, 0.5)",
            color: "rgba(0,0,0, 0.5)",
            fontWeight: "500",
            //fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
            fontSize: "14px",
            fontSmoothing: "antialiased",
            ":-webkit-autofill": {
              color: "rgba(0,0,0, 0.5)"
            },
            "::placeholder": {
              color: "rgba(0,0,0, 0.5)"
            }
          },
          invalid: {
            iconColor: "red",
            color: "red"
          }
        }
      }}
      onChange={onChange}
    />
  </div>
);

const Field = ({ label, id, type, placeholder, required, autoComplete, value, onChange }) => (
  <div className="FormRow">
    <label htmlFor={id} className="FormRowLabel">
      {label}
    </label>
    <input
      className="FormRowInput"
      id={id}
      type={type}
      placeholder={placeholder}
      required={required}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
    />
  </div>
);

const SubmitButton = ({ processing, error, children, disabled }) => (
  <button
    className={`SubmitButton ${error ? "SubmitButton--error" : ""}`}
    type="submit"
    disabled={processing || disabled}
  >
    {processing ? "Procesando..." : children}
  </button>
);

const ErrorMessage = ({ children }) => (
  <div className="ErrorMessage" role="alert">
    {children}
  </div>
);

const ResetButton = ({ onClick }) => (
  <button type="button" className="ResetButton" onClick={onClick}>
    <svg width="32px" height="32px" viewBox="0 0 32 32">
      <path
        fill="#FFF"
        d="M15,7.05492878 C10.5000495,7.55237307 7,11.3674463 7,16 C7,20.9705627 11.0294373,25 16,25 C20.9705627,25 25,20.9705627 25,16 C25,15.3627484 24.4834055,14.8461538 23.8461538,14.8461538 C23.2089022,14.8461538 22.6923077,15.3627484 22.6923077,16 C22.6923077,19.6960595 19.6960595,22.6923077 16,22.6923077 C12.3039405,22.6923077 9.30769231,19.6960595 9.30769231,16 C9.30769231,12.3039405 12.3039405,9.30769231 16,9.30769231 L16,12.0841673 C16,12.1800431 16.0275652,12.2738974 16.0794108,12.354546 C16.2287368,12.5868311 16.5380938,12.6540826 16.7703788,12.5047565 L22.3457501,8.92058924 L22.3457501,8.92058924 C22.4060014,8.88185624 22.4572275,8.83063012 22.4959605,8.7703788 C22.6452866,8.53809377 22.5780351,8.22873685 22.3457501,8.07941076 L22.3457501,8.07941076 L16.7703788,4.49524351 C16.6897301,4.44339794 16.5958758,4.41583275 16.5,4.41583275 C16.2238576,4.41583275 16,4.63969037 16,4.91583275 L16,7 L15,7 L15,7.05492878 Z M16,32 C7.163444,32 0,24.836556 0,16 C0,7.163444 7.163444,0 16,0 C24.836556,0 32,7.163444 32,16 C32,24.836556 24.836556,32 16,32 Z"
      />
    </svg>
  </button>
);

const CheckoutForm = ({ onPayment, reportID, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [retry, setRetry] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    email: "",
    phone: "",
    name: ""
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    setRetry(false);

    if (!stripe || !elements) {
      return;
    }

    /*     if (error) {
      elements.getElement("card").focus();
      console.log("here");

      return;
    } */

    if (cardComplete) {
      setProcessing(true);
    }

    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: billingDetails
    });

    const pay = await makePayment({ ...payload, pdfID: reportID, amount });

    if (pay.status !== 200) {
      setError("Ha habido un error, pruebe otra vez");
    }

    if (pay.data.error) {
      setError({ message: pay.data.message });
      setRetry(true);
    } else {
      onPayment();
      setPaymentMethod(true);
    }

    setProcessing(false);
  };

  const reset = () => {
    setError(null);
    setProcessing(false);
    setPaymentMethod(null);
    setBillingDetails({
      email: "",
      phone: "",
      name: ""
    });
  };

  return paymentMethod ? (
    <div className="Result"></div>
  ) : (
    <form className="Form" onSubmit={handleSubmit}>
      <h1 style={{ color: " #144052", fontSize: "22px", fontWeight: "bold", marginBottom: "35px" }}>Pago</h1>
      <fieldset className="FormGroup">
        <Field
          label="Nombre"
          id="name"
          type="text"
          placeholder=""
          required
          autoComplete="name"
          value={billingDetails.name}
          onChange={(e) => {
            setBillingDetails({ ...billingDetails, name: e.target.value });
          }}
        />
        <Field
          label="Email"
          id="email"
          type="email"
          placeholder=""
          required
          autoComplete="email"
          value={billingDetails.email}
          onChange={(e) => {
            setBillingDetails({ ...billingDetails, email: e.target.value });
          }}
        />
        <Field
          label="Teléfono"
          id="phone"
          type="tel"
          placeholder=""
          required
          autoComplete="tel"
          value={billingDetails.phone}
          onChange={(e) => {
            setBillingDetails({ ...billingDetails, phone: e.target.value });
          }}
        />
      </fieldset>
      <fieldset className="FormGroup">
        <CardField
          onChange={(e) => {
            setError(e.error);
            setCardComplete(e.complete);
          }}
        />
      </fieldset>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      <SubmitButton processing={processing} error={error} disabled={!stripe}>
        {retry ? "Reintentar el pago" : "Pagar " + amount + "€"}
      </SubmitButton>
    </form>
  );
};

const stripePromise = loadStripe(process.env.REACT_APP_PK_KEY);

const StripeCmp = ({ onPayment, amount }) => {
  const [reportID] = useRecoilState(reportIDSelector);

  return (
    <div className="AppWrapper">
      <Elements stripe={stripePromise}>
        <CheckoutForm onPayment={onPayment} reportID={reportID} amount={amount} />
      </Elements>
    </div>
  );
};

export default StripeCmp;
